import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  BookOpen,
  Building,
  Library,
  CheckCircle,
  Code2,
  Compass,
  Database,
  Eye,
  FileText,
  FlaskConical,
  GitBranch,
  Globe,
  Grid,
  Home,
  Layers,
  LayoutGrid,
  LayoutTemplate,
  Map as MapIcon,
  MapPin,
  Moon,
  Mountain,
  Palette,
  Search,
  Settings,
  Shapes,
  Smartphone,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Type,
  Users,
  Wrench,
  Workflow,
  Zap,
  Accessibility,
  Bot,
  Cloud,
} from 'lucide-react';
import {
  BASE,
  DEV_PORTS,
  SITE_BUILDER_URL,
  SiteMapSectionBlock,
  type SiteMapSection,
} from './siteMapShared';
import { DevServersSummaryPanel } from '../components/siteMap/DevServersSummaryPanel';

const VISUAL_LANGUAGE_VIEWER = `${BASE}visual-language/viewer.html`;
const VISUAL_LANGUAGE_FIELD_GUIDE = `${BASE}visual-language/field-guide.html`;
const VISUAL_LANGUAGE_SERVER = `${DEV_PORTS.visualLanguageServer}/viewer.html`;

/** Claritas capture destination in Notion (sibling repo ~/claritas) */
const CLARITAS_KNOWLEDGE_CANDIDATES = 'https://www.notion.so/42f2d458a7f2410a913609a17239a430';

const siteStructure: SiteMapSection[] = [
  {
    category: 'Elevation Platform',
    description: 'Knowledge system hub — design language, workbook, engines, and internal tools',
    pages: [
      {
        path: '/elevation/home',
        title: 'Elevation Home',
        description: 'Mission control — overview, stats, and entry to all Elevation areas',
        icon: Home,
        color: 'primary',
      },
      {
        path: '/elevation/design',
        title: 'Design Overview',
        description: 'Brand standards entry point and design system orientation',
        icon: Palette,
        color: 'sage',
      },
      {
        path: '/elevation/lab',
        title: 'The Lab',
        description: 'Experimental tools, engines, and standalone applications',
        icon: FlaskConical,
        color: 'amber',
      },
      {
        path: '/elevation/workbook',
        title: 'DEGAP Workbook',
        description: 'Digital workbook — Decide, Engage, Gather, Arrange, Prioritize phases',
        icon: BookOpen,
        color: 'coral',
      },
      {
        path: '/elevation/engines',
        title: 'Engines',
        description: 'Assembly, Media, Authoring, Studio, and other system engines',
        icon: Settings,
        color: 'primary',
      },
      {
        path: '/elevation/operations',
        title: 'Operations',
        description: 'Content pipeline operations — gathering through publishing',
        icon: Wrench,
        color: 'charcoal',
      },
      {
        path: '/elevation/architecture',
        title: 'Architecture',
        description: 'Technical architecture and deployment overview',
        icon: Building,
        color: 'charcoal',
      },
      {
        path: '/home/roadmap',
        title: 'Roadmap',
        description: 'Planned development and upcoming features',
        icon: Compass,
        color: 'amber',
      },
    ],
  },
  {
    category: 'Priorities.ai Product',
    description: 'Team prioritization workflow — intake, item work, sessions, and plan of record',
    pages: [
      {
        path: '/prioritization/dashboard',
        title: 'Prioritization Dashboard',
        description: 'Main product home — cycles, activity, and workspace overview',
        icon: Trophy,
        color: 'primary',
      },
      {
        path: '/prioritization/items',
        title: 'Items',
        description: 'Item inventory, triage, types, readiness, and detail views',
        icon: Layers,
        color: 'sage',
      },
      {
        path: '/prioritization/sessions',
        title: 'Sessions',
        description: 'Comparison sessions, live tools, and TV presentation mode',
        icon: Users,
        color: 'amber',
      },
      {
        path: '/prioritization/plan-of-record',
        title: 'Plan of Record',
        description: 'Organizational instrument — committed priorities and amendments',
        icon: FileText,
        color: 'coral',
      },
      {
        path: '/prioritization/tools',
        title: 'Prioritization Tools',
        description: 'Stack ranking, RICE, Eisenhower, MoSCoW, and other frameworks',
        icon: Target,
        color: 'primary',
      },
      {
        path: '/prioritization/settings',
        title: 'Workspace Settings',
        description: 'Connectors, attributes, air traffic controller, and workspace status',
        icon: Settings,
        color: 'charcoal',
      },
      {
        path: '/sessions',
        title: 'Participant Dashboard',
        description: 'Lightweight participant view for invited session contributors',
        icon: Users,
        color: 'sage',
      },
      {
        path: '/auth/portal',
        title: 'Product Portal',
        description: 'Auth landing — sign in and product entry',
        icon: Globe,
        color: 'charcoal',
      },
    ],
  },
  {
    category: 'Alternate Product UIs',
    description: 'Other shells and design surfaces for the same platform',
    pages: [
      {
        path: '/planning/dashboard',
        title: 'Planning Mode',
        description: 'Planning shell — roadmaps, strategy canvas, and planning workflows',
        icon: Compass,
        color: 'plum',
      },
      {
        path: '/apps/mandate',
        title: 'Mandate Module',
        description: 'Individual-space shell — personal mandate and embedded platform flows',
        icon: Sparkles,
        color: 'plum',
      },
      {
        path: '/prioritization/backplan',
        title: 'Back Plans',
        description: 'Backward-planning instrument inside the prioritization shell',
        icon: TrendingUp,
        color: 'teal',
      },
      {
        path: '/platform-admin',
        title: 'Platform Admin',
        description: 'Workspace administration and platform control surface',
        icon: Wrench,
        color: 'charcoal',
      },
      {
        path: '/elevation/priorities-ai-design-language',
        title: 'Product Design Hub',
        description: 'Priorities.ai v1 design canon — colors, typography, components for the dark product shell',
        icon: Moon,
        color: 'primary',
      },
      {
        path: '/elevation/design-language',
        title: 'Elevation Design Language',
        description: 'Consulting / workshop design system — parallel to the product design hub',
        icon: Palette,
        color: 'sage',
      },
      {
        path: '/elevation/lab/tool-selector',
        title: 'Tool Selector',
        description: 'Decision tree for choosing the right prioritization framework',
        icon: Target,
        color: 'amber',
      },
    ],
  },
  {
    category: 'Journey Products',
    description: 'Peak Priorities offerings for career transition and new leadership',
    pages: [
      {
        path: DEV_PORTS.whatsnext,
        title: "What's Next",
        description: 'Career transition companion — local dev :3201 (whatsnext/)',
        icon: Compass,
        color: 'teal',
        external: true,
      },
      {
        path: '/apps/first-100-days',
        title: 'First 100 Days',
        description: 'The new-leader journey on your mandate — an app in personal workspaces',
        icon: Star,
        color: 'amber',
      },
      {
        path: '/peak-home/',
        title: 'Peak Home Hub',
        description: 'Journey arc landing — /peak-home/ (bundled with SPA, same as Diagram Engine)',
        icon: Globe,
        color: 'sage',
      },
      {
        path: 'ppaf/index.html',
        title: 'PPAF Merger Page',
        description: 'Peak Priorities + Applied Frameworks narrative — repo root ppaf/',
        icon: Layers,
        color: 'coral',
        repoOnly: true,
      },
      {
        path: '/apps/whats-next',
        title: "What's Next",
        description: 'Morning Boot on your own items — an app in personal workspaces',
        icon: ArrowRight,
        color: 'charcoal',
      },
    ],
  },
  {
    category: 'Websites & Marketing',
    description: 'Public-facing sites, Astro builds, and HTML drafts',
    pages: [
      {
        path: DEV_PORTS.peakPrioritiesSite,
        title: 'Peak Priorities Site',
        description: 'Astro marketing site — local dev :4322 (peak-priorities-site/)',
        icon: Globe,
        color: 'primary',
        external: true,
      },
      {
        path: DEV_PORTS.marketingSite,
        title: 'Marketing Site',
        description: 'Section-composed Priorities.ai marketing pages — local dev :4321 (marketing-site/)',
        icon: LayoutTemplate,
        color: 'sage',
        external: true,
      },
      {
        path: 'website/peak-home.html',
        title: 'Peak Home (HTML draft)',
        description: 'Legacy static mockup — also in marketing-site-admin draft slide library',
        icon: FileText,
        color: 'charcoal',
        repoOnly: true,
      },
      {
        path: 'website/peak-platform.html',
        title: 'Peak Platform (HTML draft)',
        description: 'Platform narrative static page in website/',
        icon: FileText,
        color: 'charcoal',
        repoOnly: true,
      },
      {
        path: 'website/peak-degap.html',
        title: 'Peak DEGAP (HTML draft)',
        description: 'DEGAP methodology marketing page in website/',
        icon: BookOpen,
        color: 'charcoal',
        repoOnly: true,
      },
      {
        path: '/elevation/apps/partner-capability',
        title: 'Partner Capability App',
        description: 'Partner-facing capability narrative inside Elevation',
        icon: Users,
        color: 'amber',
      },
    ],
  },
  {
    category: 'Website Admin & Publishing',
    description: 'Compose, preview, and sync marketing site content',
    pages: [
      {
        path: SITE_BUILDER_URL,
        title: 'Site Builder',
        description: 'Marketing page composer — proxied at /site-configurator/ on :3150; direct dev :5174',
        icon: LayoutTemplate,
        color: 'primary',
        external: true,
      },
      {
        path: DEV_PORTS.siteBuilderDirect,
        title: 'Site Builder (direct)',
        description: 'marketing-site-admin Vite dev server without SPA proxy',
        icon: Wrench,
        color: 'charcoal',
        external: true,
      },
      {
        path: '/elevation/batch-process-images',
        title: 'Batch Image Processor',
        description: 'OCR and Notion intake for image batches — Prospector Engine utility',
        icon: Eye,
        color: 'sage',
      },
    ],
  },
  {
    category: 'Documentation',
    description: 'Canon, developer docs, in-app hubs, and wiki markdown',
    pages: [
      {
        path: DEV_PORTS.docsSite,
        title: 'Docs Site (Docusaurus)',
        description: 'Developer & integrator documentation — local dev :3000 (docs-site/)',
        icon: BookOpen,
        color: 'primary',
        external: true,
      },
      {
        path: '/elevation/documentation-hub',
        title: 'Documentation Hub',
        description: 'In-app catalog of system documentation and canon entries',
        icon: Library,
        color: 'sage',
      },
      {
        path: '/elevation/glossary',
        title: 'Glossary',
        description: 'Controlled vocabulary and term definitions',
        icon: BookOpen,
        color: 'amber',
      },
      {
        path: '/elevation/canon-engine',
        title: 'Canon Engine',
        description: 'Canon document viewer, synthesis, and validation',
        icon: Database,
        color: 'coral',
      },
      {
        path: `${DEV_PORTS.docsServer}/wiki/CANON.md`,
        title: 'Wiki / docs-server',
        description: 'Raw markdown from docs_prod/ — proxied at /wiki/* when docs-server :4008 is running',
        icon: FileText,
        color: 'charcoal',
        external: true,
      },
    ],
  },
  {
    category: 'Claritas',
    description:
      'Sibling repo (~/claritas) — capture, search, and continuity for Wes\'s own knowledge. Not connected to the app or its engines.',
    pages: [
      {
        path: 'claritas/CLARITAS_MAP.md',
        title: 'Start here: the Claritas map',
        description: 'What the names mean and which repo is which. Read with DEPRECATED.md, which marks every file live or retired',
        icon: BookOpen,
        color: 'primary',
        repoOnly: true,
      },
      {
        path: 'claritas/api/',
        title: 'Capture door',
        description: 'Web capture endpoint — forwards to the n8n capture workflow, which files into Notion',
        icon: Workflow,
        color: 'teal',
        repoOnly: true,
      },
      {
        path: 'claritas/ios/',
        title: 'Claritas Capture and Claritas Ask (iOS)',
        description: 'Two phone apps — Capture sends to the same workflow; Ask searches the index through the bridge',
        icon: Workflow,
        color: 'teal',
        repoOnly: true,
      },
      {
        path: 'claritas/bridge/',
        title: 'Search bridge',
        description: 'Read-only search service on the Mac — what Claritas Ask queries',
        icon: Search,
        color: 'sage',
        repoOnly: true,
      },
      {
        path: 'claritas/continuity/',
        title: 'Continuity loop',
        description: 'Ties a new thought to the work it continues and writes the return point shown at session start. Its Elevation panel is planned, not started',
        icon: GitBranch,
        color: 'amber',
        repoOnly: true,
      },
      {
        path: CLARITAS_KNOWLEDGE_CANDIDATES,
        title: 'Knowledge Candidates (Notion)',
        description: 'Where captures land. A receiving dock only — nothing in Notion processes them',
        icon: BookOpen,
        color: 'primary',
        external: true,
      },
      {
        path: 'docs/3-system/00-concept-registers/',
        title: 'Concept registers (moved here)',
        description: 'The distinction registers now live in this repo and change in the same PR as the product. The copy in claritas/registry/ is frozen',
        icon: Layers,
        color: 'plum',
        repoOnly: true,
      },
      {
        path: 'claritas/src/',
        title: 'Distinction Registry App (retired)',
        description: 'The :3220 register admin. Retired — its database no longer exists, so the page stays on "Loading…"',
        icon: Database,
        color: 'charcoal',
        repoOnly: true,
      },
      {
        path: 'claritas/notion_wiki_automation_pack_2/',
        title: 'Notion wiki pipeline (retired)',
        description: 'The six Notion agents no longer run. Only the stage prompts are still used, by the vault pipeline play',
        icon: FlaskConical,
        color: 'charcoal',
        repoOnly: true,
      },
      {
        path: 'engines/peak-knowledge-system/',
        title: 'Peak Knowledge System (PKS)',
        description: 'In this repo. Idle since July 2026 and not connected to Claritas',
        icon: Search,
        color: 'charcoal',
        repoOnly: true,
      },
      {
        path: '.seeflow/systems-map/',
        title: 'Systems Map (SeeFlow)',
        description: 'Claritas · Elevation · App diagram from June 2026 — shows the earlier Claritas, before the registers moved',
        icon: GitBranch,
        color: 'charcoal',
        repoOnly: true,
      },
    ],
  },
  {
    category: 'Diagram Engine',
    description: 'Visual language — conceptual moves, terrain, and graphical templates',
    pages: [
      {
        path: VISUAL_LANGUAGE_SERVER,
        title: 'Visual Language Server',
        description: 'Standalone Python server at :3210 — full viewer, patent figures, and all diagram assets. Start: npm run serve:visual-language',
        icon: LayoutGrid,
        color: 'primary',
        external: true,
        devServerId: 'visualLanguageServer' as const,
      },
      {
        path: VISUAL_LANGUAGE_VIEWER,
        title: 'Diagram Engine Viewer (bundled)',
        description: 'Bundled with main SPA — /visual-language/viewer.html (no separate server needed)',
        icon: LayoutGrid,
        color: 'charcoal',
        external: true,
      },
      {
        path: VISUAL_LANGUAGE_FIELD_GUIDE,
        title: 'Field Guide',
        description: 'Semantic color system and move-type reference for diagrams',
        icon: MapIcon,
        color: 'sage',
        external: true,
      },
      {
        path: '/elevation/lab/geometry-studio',
        title: 'Geometry Studio',
        description: 'Visual semantic vocabulary builder with faceted shape search',
        icon: Shapes,
        color: 'amber',
      },
      {
        path: '/elevation/lab/metaphor-studio',
        title: 'Metaphor Studio',
        description: 'Brand metaphor experiments — mountains, maps, wayfinding, terrain',
        icon: Mountain,
        color: 'coral',
      },
    ],
  },
  {
    category: 'Lab Highlights',
    description: 'Featured experimental tools — see /lab for the full inventory',
    pages: [
      {
        path: '/elevation/lab/outcome-driver-generator',
        title: 'Outcome Driver Generator',
        description: 'Structured reasons-for-change — quick driver and set builder modes',
        icon: Target,
        color: 'primary',
      },
      {
        path: '/elevation/lab/clarity-compass',
        title: 'Clarity Compass',
        description: 'Shared-reality diagnostic across seven subdomains before prioritization',
        icon: Compass,
        color: 'primary',
      },
      {
        path: DEV_PORTS.objectInspector,
        title: 'Object Inspector',
        description: 'Knowledge object browser with JQL and graph viz — apps/inspector :3400',
        icon: Search,
        color: 'sage',
        external: true,
      },
      {
        path: '/elevation/lab/consistency',
        title: 'Consistency Dashboard',
        description: 'Version drift, schema conflicts, and contradiction detection',
        icon: CheckCircle,
        color: 'amber',
      },
      {
        path: '/elevation/lab/youtube-search',
        title: 'YouTube Prioritization Search',
        description: 'Search video transcripts for prioritization-related content',
        icon: Eye,
        color: 'charcoal',
      },
      {
        path: '/elevation/lab/feature-flags',
        title: 'Feature Flags',
        description: 'Development tools for workspace and user feature toggles',
        icon: Wrench,
        color: 'charcoal',
      },
      {
        path: '/elevation/workbook/oqf',
        title: 'Open Questions Framework',
        description: 'OQF training workbook — seven modules with activities and worksheets',
        icon: BookOpen,
        color: 'coral',
      },
    ],
  },
  {
    category: 'MOF Architecture Layers',
    description: 'Four-layer Meta-Object Facility — metadata through runtime',
    pages: [
      {
        path: '/elevation/grammar',
        title: 'M3: Metadata',
        description: 'Metadata grammar and ontology — foundational structure',
        icon: Database,
        color: 'primary',
      },
      {
        path: '/elevation/models',
        title: 'M2: Models',
        description: 'Framework models and templates — conceptual layer',
        icon: Search,
        color: 'sage',
      },
      {
        path: '/elevation/templates',
        title: 'M1: Templates',
        description: 'Concrete templates and designs — implementation layer',
        icon: GitBranch,
        color: 'amber',
      },
      {
        path: '/elevation/runtime',
        title: 'M0: Runtime',
        description: 'Live instances and active implementations — execution layer',
        icon: Zap,
        color: 'coral',
      },
    ],
  },
  {
    category: 'Design System (Elevation)',
    description: 'Visual design language for consulting artefacts and workshop materials',
    pages: [
      {
        path: '/elevation/colors',
        title: 'Colors',
        description: 'Brand palettes, tints, shades, and usage guidelines',
        icon: Palette,
        color: 'sage',
      },
      {
        path: '/elevation/typography',
        title: 'Typography',
        description: 'Font families, type scale, and text style guidelines',
        icon: Type,
        color: 'amber',
      },
      {
        path: '/elevation/icon-library',
        title: 'Icon Library',
        description: 'Comprehensive icon collection with consistent style',
        icon: Shapes,
        color: 'coral',
      },
      {
        path: '/elevation/component-library',
        title: 'Component Library',
        description: 'Reusable UI components with consistent styling',
        icon: Layers,
        color: 'sage',
      },
      {
        path: '/elevation/visual-design-grammar',
        title: 'Visual Design Grammar',
        description: 'Layout principles, spacing, and visual hierarchy',
        icon: Grid,
        color: 'amber',
      },
      {
        path: '/elevation/accessibility-rules',
        title: 'Accessibility Rules',
        description: 'WCAG compliance and accessibility best practices',
        icon: Accessibility,
        color: 'sage',
      },
      {
        path: '/elevation/favicon-app-icons',
        title: 'Favicon & App Icons',
        description: 'Icon specifications for digital applications',
        icon: Smartphone,
        color: 'amber',
      },
    ],
  },
  {
    category: 'Learning & Exploration',
    description: 'Interactive learning, progress tracking, and exploration',
    pages: [
      {
        path: '/elevation/island',
        title: 'Island Explorer',
        description: 'Interactive exploration of the 12 capability dimensions',
        icon: MapPin,
        color: 'primary',
      },
      {
        path: '/elevation/mountain',
        title: 'Mountain Ascent',
        description: 'Progress through DEGAP stages and skill development',
        icon: Mountain,
        color: 'sage',
      },
      {
        path: '/elevation/progress',
        title: 'Progress Dashboard',
        description: 'Personal progress tracking and achievements',
        icon: TrendingUp,
        color: 'amber',
      },
      {
        path: '/elevation/advanced-search',
        title: 'Advanced Search',
        description: 'Search across all system content and resources',
        icon: Search,
        color: 'coral',
      },
      {
        path: '/elevation/analytics',
        title: 'Analytics',
        description: 'Learning journey analytics and insights',
        icon: BarChart3,
        color: 'primary',
      },
    ],
  },
  {
    category: 'Platform & AI (external)',
    description: 'Hosted services and assistants — open in a new tab',
    pages: [
      {
        path: 'https://n8n-service-0z9g.onrender.com',
        title: 'n8n',
        description: 'Workflow automation on Render — Claritas capture routing',
        icon: Workflow,
        color: 'coral',
        external: true,
      },
      {
        path: 'https://www.notion.so',
        title: 'Notion',
        description: 'Wiki pipeline, knowledge candidates, Claritas canonical pages',
        icon: BookOpen,
        color: 'charcoal',
        external: true,
      },
      {
        path: 'https://supabase.com/dashboard/project/bgfeuqzzuyduchrdlqyc',
        title: 'Supabase (Priorities.ai)',
        description: 'Product DB & auth — bgfeuqzzuyduchrdlqyc.supabase.co',
        icon: Database,
        color: 'sage',
        external: true,
      },
      {
        path: 'https://supabase.com/dashboard/project/nzgdxbcrzidpdvcalcov',
        title: 'Supabase (Claritas)',
        description: 'Distinction registry — nzgdxbcrzidpdvcalcov.supabase.co',
        icon: Database,
        color: 'plum',
        external: true,
      },
      {
        path: 'https://app.priorities.ai',
        title: 'Priorities.ai (production)',
        description: 'Main product app — Vite SPA, monorepo root',
        icon: Cloud,
        color: 'primary',
        external: true,
      },
      {
        path: 'https://priorities-ai-marketing.vercel.app',
        title: 'Marketing Site',
        description: 'Public Astro site — marketing-site/ (Priorities.ai + Peak Priorities)',
        icon: Cloud,
        color: 'sage',
        external: true,
      },
      {
        path: 'https://docs.priorities.ai',
        title: 'Developer Docs',
        description: 'Docusaurus docs — docs-site/ (behind sign-in)',
        icon: Cloud,
        color: 'charcoal',
        external: true,
      },
      {
        path: 'https://integrations-engine.vercel.app',
        title: 'Integrations Engine',
        description: 'Connectors API — engines/integrations-engine',
        icon: Cloud,
        color: 'coral',
        external: true,
      },
      {
        path: 'https://mobile-five-pearl.vercel.app',
        title: 'Mobile (web)',
        description: 'Expo web export — apps/mobile',
        icon: Cloud,
        color: 'plum',
        external: true,
      },
      {
        path: 'https://claritas-self.vercel.app',
        title: 'Claritas',
        description: 'Distinction registry UI — separate claritas repo',
        icon: Cloud,
        color: 'amber',
        external: true,
      },
      {
        path: 'https://vercel.com/dashboard',
        title: 'Vercel Dashboard',
        description: 'All Peak Priorities Vercel projects',
        icon: Cloud,
        color: 'charcoal',
        external: true,
      },
      {
        path: 'https://claude.ai',
        title: 'Claude',
        description: 'Anthropic — long-context analysis and writing',
        icon: Bot,
        color: 'amber',
        external: true,
      },
      {
        path: 'https://chatgpt.com',
        title: 'ChatGPT',
        description: 'OpenAI — general assistant and Codex',
        icon: Bot,
        color: 'sage',
        external: true,
      },
      {
        path: 'https://gemini.google.com',
        title: 'Gemini',
        description: 'Google — multimodal research and drafting',
        icon: Sparkles,
        color: 'primary',
        external: true,
      },
      {
        path: 'https://cursor.com',
        title: 'Cursor',
        description: 'IDE — agents, Claritas pipeline, and in-repo dev',
        icon: Code2,
        color: 'plum',
        external: true,
      },
    ],
  },
];

export function SiteMapPage() {
  const totalPages = siteStructure.reduce((total, section) => total + section.pages.length, 0);

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/elevation/home"
          className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Elevation Home
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="px-4 py-2 font-bold text-xl bg-consulting-slate-blue-100 text-consulting-slate-blue-800">
            Site Map
          </div>
          <MapIcon className="h-10 w-10 text-consulting-slate-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-charcoal-900">Peak Priorities Platform</h1>
            <p className="text-charcoal-600">
              Elevation, Priorities.ai, Claritas, journey products, websites, docs, diagram engine, and lab
            </p>
          </div>
        </div>
        <p className="text-lg text-charcoal-700">
          Navigation index for the monorepo plus Claritas (sibling repo) — same-host routes open
          in-app; sibling apps, Notion, n8n, and static drafts link out or show repo paths.
        </p>
      </div>

      <DevServersSummaryPanel />

      <div className="space-y-8">
        {siteStructure.map((section) => (
          <SiteMapSectionBlock key={section.category} section={section} />
        ))}
      </div>

      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 border border-consulting-slate-blue-200 p-6">
        <h3 className="text-lg font-bold text-charcoal-900 mb-4">At a Glance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-consulting-slate-blue-600">{totalPages}</div>
            <div className="text-sm text-charcoal-600">Linked surfaces</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-sage-600">{siteStructure.length}</div>
            <div className="text-sm text-charcoal-600">Categories</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[var(--mustard)]">:3150</div>
            <div className="text-sm text-charcoal-600">priorities.ai app dev port</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-coral-600">/lab</div>
            <div className="text-sm text-charcoal-600">Full lab inventory</div>
          </div>
        </div>
      </div>

      <div className="bg-[var(--bg-raised)] border border-charcoal-200 p-6">
        <h3 className="text-lg font-bold text-charcoal-900 mb-4">Local Dev Quick Reference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-charcoal-600">
          <ul className="space-y-1">
            <li>
              <span className="font-mono text-charcoal-800">npm run dev</span> — priorities.ai app at{' '}
              <span className="font-mono">localhost:3150</span>
            </li>
            <li>
              <span className="font-mono">/site-configurator/</span> — proxied to Site Builder :5174
            </li>
            <li>
              <span className="font-mono">/visual-language/</span> — diagram engine (bundled with SPA)
            </li>
            <li>
              <span className="font-mono">/wiki/*</span> — proxied to docs-server :4008 when running
            </li>
          </ul>
          <ul className="space-y-1">
            <li>
              <span className="font-mono">whatsnext/</span> — <span className="font-mono">:3201</span>
            </li>
            <li>
              <span className="font-mono">docs-site/</span> — <span className="font-mono">:3000</span>
            </li>
            <li>
              <span className="font-mono">peak-priorities-site/</span> —{' '}
              <span className="font-mono">:4322</span>
            </li>
            <li>
              <span className="font-mono">claritas/</span> — capture, search bridge, continuity (registry app{' '}
              <span className="font-mono">:3220</span> retired)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
